'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { NotificationsApi } from '@/lib/Apis/apis/notifications-api';
import { getApiConfiguration, getAuthToken, getCurrentConfig } from '@/lib/api-adapter/config';
import { useAuth } from './AuthProvider';
import { PreferenceUpdateItemTypeEnum, UpdateNotificationSettingsDto } from '@/lib/Apis/models';

// Re-export the enum for convenience
export type NotificationType = PreferenceUpdateItemTypeEnum;
export const NotificationType = PreferenceUpdateItemTypeEnum;
export { PreferenceUpdateItemTypeEnum };

export interface NotificationData {
    _id: string;
    type: NotificationType;
    title: string;
    body: string;
    isRead: boolean;
    createdAt: string;
    data?: {
        orderId?: string;
        productId?: string;
        [key: string]: any;
    };
}

export interface NotificationSettings {
    _id?: string;
    userId?: string;
    preferences: Array<{
        type: NotificationType;
        settings: {
            email?: boolean;
            push?: boolean;
            inApp?: boolean;
        };
    }>;
}

interface NotificationCenterContextValue {
    notifications: NotificationData[];
    unreadCount: number;
    isLoading: boolean;
    isDrawerOpen: boolean;
    settings: NotificationSettings | null;
    openDrawer: () => void;
    closeDrawer: () => void;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    deleteNotification: (id: string) => Promise<void>;
    loadMore: () => Promise<void>;
    hasMore: boolean;
    refreshNotifications: () => Promise<void>;
    updateSettings: (settings: NotificationSettings) => Promise<void>;
}

const NotificationCenterContext = createContext<NotificationCenterContextValue | undefined>(undefined);

export function useNotificationCenter() {
    const context = useContext(NotificationCenterContext);
    if (!context) {
        throw new Error('useNotificationCenter must be used within NotificationCenterProvider');
    }
    return context;
}

interface NotificationCenterProviderProps {
    children: React.ReactNode;
}

export function NotificationCenterProvider({ children }: NotificationCenterProviderProps) {
    const { isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState<NotificationData[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [settings, setSettings] = useState<NotificationSettings | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const eventSourceRef = useRef<EventSource | null>(null);
    const notificationsApi = useRef(new NotificationsApi(getApiConfiguration()));
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttemptRef = useRef(0);
    const isConnectingRef = useRef(false);
    const settingsRef = useRef<NotificationSettings | null>(null);
    const [sseVersion, setSseVersion] = useState(0); // Used to trigger reconnection

    // Keep settingsRef in sync so SSE handler can access current settings
    useEffect(() => {
        settingsRef.current = settings;
    }, [settings]);

    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        if (!isAuthenticated) return;

        try {
            const response = await notificationsApi.current.getUnreadCount();
            setUnreadCount((response.data as any)?.unreadCount || 0);
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    }, [isAuthenticated]);

    // Fetch notifications
    const fetchNotifications = useCallback(async (pageNum: number = 1, append: boolean = false) => {
        if (!isAuthenticated) return;

        setIsLoading(true);
        try {
            const response = await notificationsApi.current.getNotifications(pageNum, 20, false);
            const data = response.data as any;
            let rawNotifications = [];

            if (Array.isArray(data)) {
                rawNotifications = data;
            } else if (data?.notifications && Array.isArray(data.notifications)) {
                rawNotifications = data.notifications;
            } else if (data?.data && Array.isArray(data.data)) {
                rawNotifications = data.data;
            }


            // Map API response to our NotificationData interface
            const newNotifications = rawNotifications.map((n: any) => ({
                _id: n.id || n._id, // API returns 'id', we use '_id' internally
                type: n.type,
                title: n.title,
                body: n.body,
                isRead: n.isRead,
                createdAt: n.createdAt,
                data: n.data,
            }));

            if (append) {
                setNotifications(prev => [...prev, ...newNotifications]);
            } else {
                setNotifications(newNotifications);
            }

            setHasMore(newNotifications.length === 20);
            setPage(pageNum);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated]);

    // Fetch settings
    const fetchSettings = useCallback(async () => {
        if (!isAuthenticated) return;

        setIsLoading(true);
        try {
            const response = await notificationsApi.current.getSettings();
            const rawData = response.data as any;

            // Handle different API response structures robustly
            let finalSettings: NotificationSettings | null = null;

            if (rawData) {
                if (Array.isArray(rawData)) {
                    // Raw array [ { type: '...', settings: { ... } } ]
                    finalSettings = { preferences: rawData };
                } else if (rawData.preferences) {
                    if (Array.isArray(rawData.preferences)) {
                        // Standard nested preferences array
                        finalSettings = rawData;
                    } else if (typeof rawData.preferences === 'object') {
                        // Backend returned preferences as an object { TYPE: { email: true } }
                        // Convert to our expected array format
                        const preferences = Object.entries(rawData.preferences).map(([type, settings]) => ({
                            type: type as any,
                            settings: settings as any
                        }));
                        finalSettings = { ...rawData, preferences };
                    }
                } else if (rawData.data) {
                    const d = rawData.data;
                    if (d.preferences) {
                        if (Array.isArray(d.preferences)) {
                            finalSettings = d;
                        } else if (typeof d.preferences === 'object') {
                            const preferences = Object.entries(d.preferences).map(([type, settings]) => ({
                                type: type as any,
                                settings: settings as any
                            }));
                            finalSettings = { ...d, preferences };
                        }
                    } else if (Array.isArray(d)) {
                        finalSettings = { preferences: d };
                    }
                } else if (typeof rawData === 'object' && !rawData.preferences) {
                    // Maybe it's just the preferences object directly { TYPE: { email: true } }
                    const preferences = Object.entries(rawData).map(([type, settings]) => ({
                        type: type as any,
                        settings: settings as any
                    }));
                    if (preferences.length > 0 && preferences[0].settings && typeof preferences[0].settings === 'object') {
                        finalSettings = { preferences };
                    } else {
                        finalSettings = rawData;
                    }
                } else {
                    finalSettings = rawData;
                }
            }
            setSettings(finalSettings);
        } catch (error) {
            console.error('Failed to fetch settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated]);

    // SSE Connection for real-time notifications
    useEffect(() => {
        // Clean up any pending reconnection timer
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (!isAuthenticated) {
            // Close existing connection if user logs out
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            isConnectingRef.current = false;
            reconnectAttemptRef.current = 0;
            return;
        }

        // Prevent duplicate connections
        if (isConnectingRef.current || eventSourceRef.current?.readyState === EventSource.OPEN) {
            return;
        }

        const config = getCurrentConfig();
        const token = getAuthToken();

        if (!token) return;

        isConnectingRef.current = true;

        // Ensure no double slashes in URL
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl.slice(0, -1) : config.apiBaseUrl;
        const sseUrl = `${baseUrl}/notifications/stream?token=${encodeURIComponent(token)}`;

        try {
            const eventSource = new EventSource(sseUrl);
            eventSourceRef.current = eventSource;

            eventSource.onopen = () => {
                // Connection successful - reset reconnection attempts
                reconnectAttemptRef.current = 0;
                isConnectingRef.current = false;
            };

            eventSource.onmessage = (event) => {
                try {
                    const rawNotification = JSON.parse(event.data);

                    // Map API response to our NotificationData interface
                    const notification: NotificationData = {
                        _id: rawNotification.id || rawNotification._id,
                        type: rawNotification.type,
                        title: rawNotification.title,
                        body: rawNotification.body,
                        isRead: rawNotification.isRead || false,
                        createdAt: rawNotification.createdAt,
                        data: rawNotification.data,
                    };

                    const typeSetting = settingsRef.current?.preferences?.find(p => p.type === notification.type);
                    const pushEnabled = typeSetting?.settings?.push !== false;

                    // Add to notification center only if push is enabled for this type (default true)
                    if (pushEnabled) {
                        setNotifications(prev => [notification, ...prev]);
                        setUnreadCount(prev => prev + 1);
                    }

                    // Browser notification disabled — inApp setting is reserved for the mobile app
                    if (false && 'Notification' in window && Notification.permission === 'granted') {
                        new Notification(notification.title, {
                            body: notification.body,
                            icon: '/icon.png',
                        });
                    }
                } catch (error) {
                    console.error('Failed to parse SSE notification:', error);
                }
            };

            eventSource.onerror = () => {
                // Close the failed connection
                eventSource.close();
                eventSourceRef.current = null;
                isConnectingRef.current = false;

                // Calculate exponential backoff delay (1s, 2s, 4s, 8s, ... max 30s)
                const baseDelay = 1000;
                const maxDelay = 30000;
                const delay = Math.min(baseDelay * Math.pow(2, reconnectAttemptRef.current), maxDelay);
                reconnectAttemptRef.current++;

                // Only log occasional reconnection attempts to reduce console noise
                if (reconnectAttemptRef.current <= 3 || reconnectAttemptRef.current % 5 === 0) {
                    console.warn(`SSE connection error. Reconnecting in ${delay / 1000}s (attempt ${reconnectAttemptRef.current})`);
                }

                // Schedule reconnection
                reconnectTimeoutRef.current = setTimeout(() => {
                    if (isAuthenticated && getAuthToken()) {
                        // Trigger effect to recreate connection
                        setSseVersion(v => v + 1);
                    }
                }, delay);
            };

            return () => {
                eventSource.close();
                eventSourceRef.current = null;
                isConnectingRef.current = false;
            };
        } catch (error) {
            console.error('Failed to establish SSE connection:', error);
            isConnectingRef.current = false;
        }
    }, [isAuthenticated, sseVersion]);

    // Initial data fetch
    useEffect(() => {
        if (isAuthenticated) {
            fetchUnreadCount();
            fetchNotifications(1);
            fetchSettings();
        } else {
            // Reset state when logged out
            setNotifications([]);
            setUnreadCount(0);
            setSettings(null);
            setPage(1);
            setHasMore(true);
        }
    }, [isAuthenticated, fetchUnreadCount, fetchNotifications, fetchSettings]);

    // Request notification permission
    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    const openDrawer = useCallback(() => {
        console.log('[NotificationCenter] Drawer opened', {
            totalNotifications: notifications.length,
            unreadCount,
            settings: settings?.preferences?.map(p => ({
                type: p.type,
                push: p.settings?.push,
                inApp: p.settings?.inApp,
                email: p.settings?.email,
            })),
            notifications: notifications.map(n => ({
                id: n._id,
                type: n.type,
                title: n.title,
                isRead: n.isRead,
            })),
        });
        setIsDrawerOpen(true);
    }, [notifications, unreadCount, settings]);

    const closeDrawer = useCallback(() => {
        setIsDrawerOpen(false);
    }, []);

    const markAsRead = useCallback(async (id: string) => {
        try {
            await notificationsApi.current.markAsRead(id);

            // Update local state
            setNotifications(prev =>
                prev.map(n => (n._id === id ? { ...n, isRead: true } : n))
            );

            // Decrement unread count
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        try {
            await notificationsApi.current.markAllAsRead();

            // Update local state
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    }, []);

    const deleteNotification = useCallback(async (id: string) => {
        try {
            await notificationsApi.current.deleteNotification(id);

            // Update local state
            const notification = notifications.find(n => n._id === id);
            setNotifications(prev => prev.filter(n => n._id !== id));

            // Decrement unread count if notification was unread
            if (notification && !notification.isRead) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    }, [notifications]);

    const loadMore = useCallback(async () => {
        if (!hasMore || isLoading) return;
        await fetchNotifications(page + 1, true);
    }, [hasMore, isLoading, page, fetchNotifications]);

    const refreshNotifications = useCallback(async () => {
        await fetchNotifications(1, false);
        await fetchUnreadCount();
    }, [fetchNotifications, fetchUnreadCount]);

    const updateSettings = useCallback(async (newSettings: NotificationSettings) => {
        try {
            // Clean the settings object to match UpdateNotificationSettingsDto
            // and remove any frontend-only fields that might cause validation errors
            const payload: UpdateNotificationSettingsDto = {
                _id: newSettings._id,
                preferences: newSettings.preferences.map(p => ({
                    type: p.type,
                    settings: {
                        email: p.settings?.email,
                        push: p.settings?.push,
                        inApp: p.settings?.inApp,
                    }
                }))
            };

            await notificationsApi.current.updateSettings(payload);

            // Refetch settings to ensure we have the server-persisted state
            await fetchSettings();
        } catch (error) {
            console.error('Failed to update settings:', error);
            throw error;
        }
    }, [fetchSettings]);

    // Filter notifications by push settings - only show types where push is enabled
    const filteredNotifications = settings?.preferences
        ? notifications.filter(n => {
            const typeSetting = settings.preferences.find(p => p.type === n.type);
            // If no setting found for this type, default to showing it
            if (!typeSetting) return true;
            return typeSetting.settings?.push !== false;
        })
        : notifications;

    const filteredUnreadCount = unreadCount;

    const value: NotificationCenterContextValue = {
        notifications: filteredNotifications,
        unreadCount: filteredUnreadCount,
        isLoading,
        isDrawerOpen,
        settings,
        openDrawer,
        closeDrawer,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        loadMore,
        hasMore,
        refreshNotifications,
        updateSettings,
    };

    return (
        <NotificationCenterContext.Provider value={value}>
            {children}
        </NotificationCenterContext.Provider>
    );
}
